import { useDroppable } from "@dnd-kit/core";
import { Loading, useTranslation, useUrlParam } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { useUIPermission } from "@opendash/plugin-parse";
import { Button, Empty, Flex, Layout, Tag, Typography } from "antd";
import { Content, Header } from "antd/lib/layout/layout";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect } from "react";
import styled from "styled-components";
import {
  FetchResponse,
  FilterDrawer,
  FilterSortTags,
  TicketContextProvider,
  TicketData,
  TicketManager,
  TicketManagerConfig,
  TicketTileKanban,
  useTicketManager,
} from "../..";
import { OpenService } from "../../../../types/config";
import { ColumnProvider } from "../context/ColumnContext";
import { KanbanContext } from "../context/KanbanContext";

const HeadlineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  align-content: center;
`;
const HeadlineRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: stretch;
  align-content: center;
`;
const HeadlineItem = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: stretch;
  align-content: center;
`;

export const KanbanColumn = observer(
  (props: {
    id: string;
    title: string;
    openSaveModal?: (flag: boolean) => void;
    ticketManagerConfig?: TicketManagerConfig;
  }) => {
    const t = useTranslation();
    const kanbanManager = useContext(KanbanContext);
    const [searchString] = useUrlParam<string>("searchTickets", undefined);
    const [showFilterDrawer, setShowFilterDrawer] = React.useState(false);
    const [ticketResponse, setTicketResponse] = React.useState<FetchResponse>({
      data: [],
      total: 0,
      success: true,
      error: null,
    });

    const { setNodeRef, isOver } = useDroppable({
      id: props.id,
    });

    const canCreateTicket = useUIPermission([
      OpenService.Permissions.Create.ticket,
    ]);

    const searchTicketManager = useTicketManager();
    const generalTicketManager = useTicketManager(
      kanbanManager.getDefaultTicketFilterConfig()
    );
    const filterTicketManager = useTicketManager(props.ticketManagerConfig);
    const dataTicketManager = useTicketManager();

    // combines the ticket managers to get the tickets for the state and the other fields
    useEffect(() => {
      // set the config for the general ticket manager to make sure it has the correct filters
      generalTicketManager.setConfig(
        toJS(kanbanManager.getDefaultTicketFilterConfig())
      );

      searchTicketManager.addFilter("title", {
        attribute: "title",
        value: searchString || "",
      });

      const managersToFetch = [
        generalTicketManager,
        dataTicketManager,
        filterTicketManager,
      ];

      // if a search string is set, we also want to only fetch the tickets that match the search string
      if (searchString) managersToFetch.push(searchTicketManager);

      TicketManager.And(...managersToFetch).then((response) =>
        setTicketResponse(response)
      );
    }, [kanbanManager.getReload(props.id)]);

    const hasMore = ticketResponse.data?.length < ticketResponse.total;

    if (kanbanManager.hideEmpty && ticketResponse.data.length === 0) {
      return <></>;
    }

    return (
      <div ref={setNodeRef} style={{ height: "100%" }}>
        <Layout
          style={{
            height: "inherit",
            padding: "12px",
            backgroundColor: "var(--opendash-colors-background)",
            border: isOver
              ? "2px solid var(--opendash-colors-primary)"
              : "1px solid var(--opendash-color-darkgray)",
          }}
        >
          <Header
            style={{
              backgroundColor: "white",
              padding: 0,
              textAlign: "center",
              height: "auto",
            }}
          >
            <HeadlineWrapper>
              <HeadlineRow
                style={{
                  flexWrap: "nowrap",
                }}
              >
                <HeadlineItem>
                  <Typography.Title
                    level={3}
                    style={{
                      display: "block",
                      padding: 10,
                    }}
                  >
                    {props.title}
                  </Typography.Title>
                </HeadlineItem>
                <HeadlineItem>
                  <Button
                    type="text"
                    icon={<Icon icon="fa:filter"></Icon>}
                    style={{
                      display: "block",
                      height: "100%",
                      color:
                        dataTicketManager.getSortBy() ||
                        dataTicketManager.hasFilterActive
                          ? "orange"
                          : "#000000",
                    }}
                    onClick={(e) => setShowFilterDrawer(true)}
                  />
                </HeadlineItem>
              </HeadlineRow>
              <HeadlineRow>
                <HeadlineItem>
                  <Tag style={{ marginBottom: 10 }}>
                    {t("maintenance:monitor.column.ticketcount.label")}:{" "}
                    {ticketResponse.data?.length} / {ticketResponse.total}
                  </Tag>

                  <FilterSortTags
                    sortManager={dataTicketManager}
                    filterManager={dataTicketManager}
                    reload={() => kanbanManager.reload(props.id)}
                  />
                </HeadlineItem>
              </HeadlineRow>
            </HeadlineWrapper>
          </Header>
          <Content>
            {ticketResponse?.data.map((data: TicketData) => {
              return (
                <ColumnProvider value={props.id} key={data.ticket.id}>
                  <TicketContextProvider value={data}>
                    <TicketTileKanban
                      key={data.ticket.id}
                      ticketManager={dataTicketManager}
                    />
                  </TicketContextProvider>
                </ColumnProvider>
              );
            })}

            {ticketResponse?.data?.length <= 0 && (
              <>
                {dataTicketManager.loading || filterTicketManager.loading ? (
                  <Loading message={undefined} />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("maintenance:home.empty")}
                  />
                )}
              </>
            )}

            <Flex align="center" justify="stretch" vertical gap="8px">
              {hasMore && (
                <Button
                  style={{ width: "100%" }}
                  onClick={() => {
                    generalTicketManager.incrementPage();
                    kanbanManager.reload(props.id);
                  }}
                  disabled={generalTicketManager.loading}
                  loading={
                    dataTicketManager.loading || filterTicketManager.loading
                  }
                >
                  <Typography.Text>
                    {t("maintenance:monitor.column.loadMore")}
                  </Typography.Text>
                </Button>
              )}
              {canCreateTicket && props.openSaveModal && (
                <Button
                  style={{ width: "100%" }}
                  onClick={() => props.openSaveModal(true)}
                  type="primary"
                >
                  <Icon icon="fa:plus" />
                </Button>
              )}
            </Flex>
          </Content>
        </Layout>
        <FilterDrawer
          showGroupSwitch={false}
          showSubordinateSwitch={false}
          open={showFilterDrawer}
          setOpen={(b) => setShowFilterDrawer(b)}
          sortManager={dataTicketManager}
          filterManager={dataTicketManager}
          refetch={() => kanbanManager.reload(props.id)}
        />
      </div>
    );
  }
);
